import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef
} from 'react';
import toast from 'react-hot-toast';
import { useApi } from './ApiContext'; 

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const { api, API_BASE_URL } = useApi();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddToCartPopup, setShowAddToCartPopup] = useState(false);
    const [saving, setSaving] = useState(false);
    const [recentlyRemoved, setRecentlyRemoved] = useState(null);
    
    // Gunakan useRef untuk elak autosave trigger masa initial load
    const isInitialMount = useRef(true);

    // ➡️ LOAD CART DARI BACKEND
    const fetchCartFromBackend = useCallback(async () => {
        if (!API_BASE_URL || !api) return;

        setLoading(true);
        try {
            const response = await api.get('/cart/load');
            setCartItems(response.data || []);
        } catch (error) {
            console.error('Error loading cart:', error);
            setCartItems([]);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, api]);

    useEffect(() => {
        fetchCartFromBackend();
    }, [fetchCartFromBackend]);

    // ➡️ AUTOSAVE CART KE BACKEND
    useEffect(() => {
        // Skip kalau initial load atau api belum sedia
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (loading || !API_BASE_URL || !api) return; 

        setSaving(true);
        const timeout = setTimeout(async () => {
            try {
                await api.post('/cart/save', { cartItems });
                console.log('Cart saved:', cartItems);
            } catch (error) {
                // ➡️ AUTH CHECK: Abaikan error jika sesi dah tamat/logout (401 atau 403)
                if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 400) {
                    console.log('Session expired, skipping autosave.');
                    return;
                }
                
                console.error('Error saving cart:', error);
                toast.error('Failed to save cart', { id: 'save-fail' });
            } finally {
                setSaving(false);
            }
        }, 500);

        return () => clearTimeout(timeout);
    }, [cartItems, loading, API_BASE_URL, api]);

    const addToCart = (product) => {
        const duration = product.selected_duration;
        const key = `${product.id}-${duration}`;

        if (!duration || !product.price || !product.stock) {
            toast.error('This purchase option is available for reseller accounts only.', { id: 'invalid-add' });
            return;
        }

        setCartItems((prevItems) => {
            const existingIndex = prevItems.findIndex(
                (item) => `${item.id}-${item.selected_duration}` === key
            );

            if (existingIndex !== -1) {
                const existingItem = prevItems[existingIndex];
                if (existingItem.quantity >= existingItem.stock) {
                    toast.error(`Only ${existingItem.stock} items available`, { id: `max-${key}` });
                    return prevItems;
                }
                const updatedItems = [...prevItems];
                updatedItems[existingIndex] = {
                    ...existingItem,
                    quantity: Math.min(existingItem.quantity + 1, existingItem.stock)
                };
                toast.success('Quantity increased in cart', { id: `add-${key}` });
                return updatedItems;
            }

            toast.success('Item added to cart', { id: `add-${key}` });
            return [...prevItems, {
                ...product,
                product_data: product.product_data || {},
                quantity: 1
            }];
        });

        setShowAddToCartPopup(true);
        setTimeout(() => setShowAddToCartPopup(false), 2000);
    };

    const updateQuantity = (index, quantity) => {
        setCartItems((prevItems) => {
            const updated = [...prevItems];
            const maxQty = updated[index].stock;

            if (quantity <= 0) {
                if (window.confirm('Quantity is 0. Remove item from cart?')) {
                    updated.splice(index, 1);
                    return updated;
                }
                return prevItems;
            }

            if (quantity > maxQty) {
                toast.error(`Only ${maxQty} items available`, { 
                    id: `limit-${updated[index].id}-${updated[index].selected_duration}` 
                });
                updated[index].quantity = maxQty;
            } else {
                updated[index].quantity = quantity;
            }
            return updated;
        });
    };

    const removeItem = (index) => {
        setCartItems((prevItems) => {
            const removed = prevItems[index];
            if (!removed) return prevItems;

            const key = `remove-${removed.id}-${removed.selected_duration}`;
            toast.dismiss(key);
            setRecentlyRemoved({ item: removed, index });

            toast((t) => (
                <span>
                    Removed item 
                    <button
                        onClick={() => {
                            setCartItems((items) => {
                                const restored = [...items];
                                restored.splice(index, 0, removed);
                                return restored;
                            });
                            toast.dismiss(t.id);
                        }}
                        className="undo_button"
                    >
                        Undo
                    </button>
                </span>
            ), { duration: 5000, id: key });

            return prevItems.filter((_, i) => i !== index);
        });
    };

    const updateDuration = (index, newDuration) => {
        setCartItems((prevItems) => {
            const updatedItems = [...prevItems];
            const itemToUpdate = updatedItems[index];
            const newData = itemToUpdate.product_data?.[newDuration];

            if (!newData) return prevItems;

            const key = `${itemToUpdate.id}-${newDuration}`;
            const adjustedQuantity = Math.min(itemToUpdate.quantity, newData.count);

            const updatedItem = {
                ...itemToUpdate,
                selected_duration: newDuration,
                price: newData.price,
                stock: newData.count,
                quantity: adjustedQuantity,
            };

            const duplicateIndex = updatedItems.findIndex((item, i) =>
                i !== index && `${item.id}-${item.selected_duration}` === key
            );

            if (duplicateIndex !== -1) {
                const existingItem = updatedItems[duplicateIndex];
                let totalQuantity = existingItem.quantity + adjustedQuantity;

                if (totalQuantity > newData.count) {
                    totalQuantity = newData.count;
                    toast(`Total adjusted to ${newData.count} for ${newDuration}`, { id: `adjusted-${key}` });
                }

                updatedItems[duplicateIndex] = { ...existingItem, quantity: totalQuantity };
                updatedItems.splice(index, 1);
                toast.success('Variants merged', { id: `merged-${key}` });
            } else {
                updatedItems[index] = updatedItem;
                toast.success('Variant updated', { id: `updated-${key}` });
            }
            return updatedItems;
        });
    };

    const getRemainingStock = (productId, duration, totalStock = 0) => {
        const inCart = cartItems
            .filter(item => item.id === productId && item.selected_duration === duration)
            .reduce((sum, item) => sum + item.quantity, 0);
        return totalStock - inCart;
    };

    const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider
            value={{
                cartItems,
                loading,
                addToCart,
                updateQuantity,
                removeItem,
                cartCount,
                fetchCartFromBackend,
                showAddToCartPopup,
                updateDuration,
                saving,
                getRemainingStock,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);